/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';

import { isEmpty } from 'lodash';
import Types from '../constants/actionTypes';
import * as API from '../utils/api';
import { applicationError, notificationAction } from './notificationActions';
import { getCadence } from './cadenceActions';
import Store from '../store/store';
import { selectedSpaceOnAuth } from './workPackageActions';
import { resolveUser } from '../utils/reduxHelpers';
import {
  populateViewState,
  populatePlanwallStates,
  populateSavedKanbanFilters
} from './savedViewActions';

const dataFormatter = new Jsona();
const DEMO = process.env.DEMO === 'true';

export const updateUserStore = (user, action) => (dispatch) => {
  switch (action) {
    case 'update':
      return dispatch({ type: Types.UPDATE_USER, user });
    case 'create':
      return dispatch({ type: Types.ADD_USER, user });
  }
};

export function fetchUsers(spaceChange) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER });
    return API.get('owners')
      .then((response) => {
        const users = dataFormatter.deserialize(response);
        users.forEach((user) => {
          resolveUser(user);
        });
        dispatch({ type: Types.RECEIVE_USER, users });
      })
      .then(() => {
        const currentUser = Store.getState().userReducer.user;
        if (currentUser.saved_space_view) {
          const { saved_kanban_filters, saved_space } =
            currentUser.saved_space_view;

          if (!spaceChange) {
            const {
              planwall_defaults,
              saved_views = [],
              previous_views
            } = currentUser.saved_space_view;

            let newSpace = saved_space;
            if (isEmpty(saved_space) || saved_space === undefined) {
              newSpace = { id: 'unassigned', initialSpace: true };
            }
            dispatch(selectedSpaceOnAuth(newSpace));
            dispatch(populateViewState(saved_views, previous_views));
            if (!isEmpty(planwall_defaults)) {
              dispatch(populatePlanwallStates(planwall_defaults));
            }
          }
          if (
            saved_kanban_filters &&
            !isEmpty(saved_kanban_filters) &&
            saved_space
          ) {
            dispatch(
              populateSavedKanbanFilters(saved_kanban_filters, saved_space)
            );
          }
        }
      })
      .then(() => dispatch(getCadence()))
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_USER_FAILED });
      });
  };
}

export function activateSpacesSpinner(bool) {
  return (dispatch) => {
    dispatch({ type: Types.SPACES_SPINNER, activeSpacesSpinner: bool });
  };
}

export function fetchUserInfo() {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER });
    return API.get('profile')
      .then((response) => {
        const user = dataFormatter.deserialize(response);
        resolveUser(user);
        dispatch({ type: Types.GET_USER_PROFILE, user });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export const change401StatusToFalse = () => (dispatch) => {
  dispatch({ type: Types.RESET_USER_401 });
};

export function login(details) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER });
    dispatch({ type: Types.LOGIN_ERRORS, errors: null });
    return API.post('auth/sign_in', details)
      .then(() => dispatch({ type: Types.USER_LOGIN }))
      .then(() => true)
      .catch((error) => {
        dispatch({
          type: Types.LOGIN_ERRORS,
          errors: error.message
            ? error.message
            : 'Invalid credentials. Please try again.'
        });
        return false;
      });
  };
}

export function setAuth(userAuth) {
  return (dispatch) =>
    dispatch({ type: Types.USER_AUTH_UPDATE, user: userAuth });
}

const disconnectActionCable = () => {
  window.App.heartbeat.stopHeartbeatMonitor();
  window.App.subscriptions.subscriptions.forEach((subscription) => {
    subscription.unsubscribe();
  });
};

export function logout() {
  return (dispatch) =>
    API.del('auth/sign_out')
      .then(() => dispatch({ type: Types.SET_CADENCE, cadence: {} }))
      .then(() => {
        dispatch({ type: Types.USER_LOGOUT });
        dispatch({ type: Types.SET_SELECTED_SPACE, selectedSpace: {} });
        disconnectActionCable();
      })
      .catch((error) => {
        dispatch({ type: Types.SET_CADENCE, cadence: {} });
        dispatch({ type: Types.USER_LOGOUT });
        dispatch({ type: Types.SET_SELECTED_SPACE, selectedSpace: {} });
        dispatch(applicationError(error));
        disconnectActionCable();
      });
}

export function uploadUserImage(file, fileUrl, userId) {
  const data = new FormData();
  const fileCopy = file;
  fileCopy.path = fileUrl;
  data.append('user[image]', fileCopy);
  data.append('user[id]', userId);
  return (dispatch) => {
    dispatch({ type: Types.UPLOAD_PROFILE_PICTURE });
    return API.filePost(`users/${userId}/upload_picture`, data, 'PUT')
      .then((response) => response.json())
      .then((response) => {
        const user = dataFormatter.deserialize(response);
        resolveUser(user);
        dispatch({ type: Types.GET_USER_PROFILE, user });
      })
      .catch((error) => {
        dispatch({ type: Types.UPDATE_USER_ERROR, formError: error });
        dispatch(applicationError(error));
      });
  };
}

// update your own profile
export function updateCurrentUser(data) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER_UPDATE });
    return API.put('profile/update', data)
      .then((response) => response.json())
      .then((response) => {
        const user = dataFormatter.deserialize(response);
        resolveUser(user);
        dispatch({ type: Types.GET_USER_PROFILE, user });
        dispatch({ type: Types.UPDATE_TEAM_MEMBER, user });
        dispatch({ type: Types.CLOSE_MODAL, id: 'user' });
        dispatch(
          notificationAction(`Your Profile was successfully updated`),
          3
        );
      })
      .catch((error) => {
        dispatch({ type: Types.UPDATE_USER_ERROR, formError: error });
      });
  };
}

// update another users profile
export function updateUser(data, id) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_USER_UPDATE });

    // don't pass the whole team or product when setting default
    const payload = { ...data };
    if (payload.user.default_team)
      payload.user.default_team = { id: payload.user.default_team.id };
    if (payload.user.default_product)
      payload.user.default_product = { id: payload.user.default_product.id };
    // no need to pass teams, we have team_ids.  Not used by this endpoint anyway.
    delete payload.user.teams;

    return API.put(`users/update_user/${id}`, payload)
      .then((response) => response.json())
      .then((response) => {
        const user = dataFormatter.deserialize(response);
        resolveUser(user);
        dispatch({ type: Types.UPDATE_USER, user });
        dispatch({ type: Types.UPDATE_TEAM_MEMBER, user });
        dispatch({ type: Types.CLOSE_MODAL, id: 'user' });
        dispatch(
          notificationAction(`This user's Profile was successfully updated`, 3)
        );
      })
      .catch((error) => {
        dispatch({ type: Types.UPDATE_USER_ERROR, formError: error });
      });
  };
}

export function getAuthorizedProductIds(userId) {
  return (dispatch) =>
    API.get(`users/authorized_products/${userId}`).then((response) => {
      dispatch({
        type: Types.AUTHORIZED_PRODUCT_IDS,
        userId,
        productIds: response.authorized_product_ids
      });
    });
}

export function updateUserSpaceView(data, action, view = {}) {
  if (!localStorage.authenticated) return (dispatch) => {};
  return async (dispatch) =>
    API.put(`profile/update`, data)
      .then((response) => response.json())
      .then((response) => {
        const user = dataFormatter.deserialize(response);
        dispatch({ type: Types.UPDATE_USER, user });
        if (action === 'create') {
          dispatch({ type: Types.CREATE_SAVED_VIEW, newView: view });
        }
        if (action === 'delete') {
          dispatch({ type: Types.DELETE_SAVED_VIEW, view });
        }
      })
      .catch((error) => {
        dispatch({ type: Types.UPDATE_USER_ERROR, formError: error });
      });
}

export const resetPassword = (email) => (dispatch) => {
  dispatch({ type: Types.REQUEST_USER_UPDATE });
  return API.postSkipAuthHeaders('auth/password', {
    email,
    redirect_url: `${process.env.HOST_BASE_URL}/reset_password?email=${email}`
  })
    .then((response) => response.json())
    .then(() => {
      dispatch({ type: Types.USER_NOT_FETCHING });
      dispatch(
        notificationAction(
          'The password reset instructions have been sent by email',
          3
        )
      );
      dispatch({ type: Types.RESET_PASSWORD_ERRORS, errors: {} });
      return true;
    })
    .catch((error) => {
      dispatch({ type: Types.RESET_PASSWORD_ERRORS, errors: error.errors });
      return false;
    });
};

export const confirmEmail = (email) => (dispatch) => {
  dispatch({ type: Types.REQUEST_USER_UPDATE });
  return API.postSkipAuthHeaders('resend_confirmation', {
    email
  })
    .then((response) => response.json())
    .then(() => {
      dispatch({ type: Types.USER_NOT_FETCHING });
      dispatch(
        notificationAction('The confirmation link has been sent by email', 3)
      );
      return true;
    })
    .catch((error) => {
      dispatch({ type: Types.USER_NOT_FETCHING });
      dispatch(notificationAction(error.errors[0], true));
      dispatch(fetchUsers(false));
      return false;
    });
};

export const updatePassword = (data, headers) => (dispatch) => {
  dispatch({ type: Types.REQUEST_USER_UPDATE });
  return API.putWithCustomHeaders(`auth/password`, data, headers)
    .then((response) => response.json())
    .then(() => {
      dispatch({ type: Types.USER_NOT_FETCHING });
      dispatch({ type: Types.RESET_PASSWORD_ERRORS, errors: {} });
      return true;
    })
    .catch((error) => {
      dispatch({ type: Types.RESET_PASSWORD_ERRORS, errors: error.errors });
      return false;
    });
};

export function updateCapacity(userId, capacity) {
  return (dispatch) =>
    API.put('capacity', {
      user_id: userId,
      team_assignment: { capacity }
    })
      .then((response) => response.json())
      .then((response) =>
        dispatch({
          type: Types.UPDATE_USER_CAPACITY,
          capacity: response.capacity,
          userId
        })
      );
}

export function userGot401() {
  return (dispatch) => {
    disconnectActionCable();
    dispatch({ type: Types.USER_GOT_401 });
    dispatch({ type: Types.CLOSE_ALL_MODALS });
    dispatch({ type: Types.USER_LOGOUT, got401: true });
    if (!DEMO) {
      dispatch({
        type: Types.UNAUTHORIZED_ERROR,
        message: 'Your session is no longer active. Please log in again!'
      });
    }
  };
}

export function updateFormError(errors) {
  return (dispatch) =>
    dispatch({ type: Types.UPDATE_USER_ERROR, formError: { errors } });
}
